import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'; // Import useNavigate and useLocation
import { Helmet } from 'react-helmet-async'; // Import Helmet for SEO
import Layout from '../../components/ui/Layout';
import { db } from '../../firebase';
import { collection, doc, getDoc, getDocs, query, where, runTransaction, addDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../components/context/AuthContext';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation'; // Import Breadcrumb
import ShareButton from '../../components/ui/ShareButton';
import { SITE_URL, getProductShareSlug } from '../../constants/site';
import { Button, Container, Row, Col, Form, Image, Modal, ListGroup, Badge } from 'react-bootstrap';
import { createLogger } from '../../utils/logger';
import { useNotification } from '../../components/ui/Notification';
import ProductImage from '../../components/ui/ProductImage';
import { formatCartItems, calculateTotalItems, calculateTotalPrice, getActiveCartInstanceId } from '../../utils/cartHelpers';
import AddToShoppingListModal from '../../components/shoppingList/AddToShoppingListModal';

const logger = createLogger('SupermarketProducts');

// Helper to check if user can moderate reviews for this supermarket
function canModerateReviews(userRole, currentUser, supermarket, employeeSupermarketId) {
  if (!userRole || !currentUser || !supermarket) return false;
  if (userRole === 'Admin') return true;
  if (userRole === 'Employee') {
    // Use employeeSupermarketId from AuthContext
    if (employeeSupermarketId && employeeSupermarketId === supermarket.id) {
      return true;
    }
  }
  return false;
}

// Helper to check if user can see hidden reviews for this supermarket
function canSeeHiddenReviews(userRole, currentUser, supermarket, employeeSupermarketId) {
  return canModerateReviews(userRole, currentUser, supermarket, employeeSupermarketId);
}

export default function SupermarketProducts() {
  const { supermarketId, seoName, slug } = useParams();
  const effectiveSeoName = seoName ?? slug; 
  const [products, setProducts] = useState([]);
  const [supermarket, setSupermarket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seoSupermarketId, setSeoSupermarketId] = useState(null);
  const { currentUser, userRole, employeeSupermarketId, userName } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate(); // For Back button
  const location = useLocation(); // For SEO URL handling
  
  // Debug logging for routing
  useEffect(() => {
    logger.debug('[SupermarketProducts] Component mounted with params:', { supermarketId, effectiveSeoName });
    logger.debug('[SupermarketProducts] Current location:', location.pathname);
  }, [supermarketId, effectiveSeoName, location.pathname]);

  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviews, setReviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [hidingReviewIds, setHidingReviewIds] = useState([]);
  const [supermarketImage, setSupermarketImage] = useState(''); // For supermarket image
  
  // Discount deals modal state
  const [showDealsModal, setShowDealsModal] = useState(false);
  const [selectedProductDeals, setSelectedProductDeals] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedProductPrice, setSelectedProductPrice] = useState(0);

  // Add to shopping list modal state
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [listProductContext, setListProductContext] = useState(null);
  
  // Pagination states
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const productsPerPage = 12; // 3 rows × 4 items = 12 products

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/supermarkets", label: "Supermarkets" },
    { label: supermarket?.display_name || "Supermarket Products" } 
  ];

  // Handle viewing discount deals
  const handleViewDeals = (product) => {
    const deals = product.inventoryDetails?.discount_deals || [];
    setSelectedProductDeals(deals);
    setSelectedProductName(product.display_name || 'Unknown Product');
    setSelectedProductPrice(product.inventoryDetails?.sale_price || 0);
    setShowDealsModal(true);
  };

  const handleCloseDealsModal = () => {
    setShowDealsModal(false);
    setSelectedProductDeals([]);
    setSelectedProductName('');
    setSelectedProductPrice(0);
  };

  const handleAddToCart = async (productId, productDetails) => {
    if (!currentUser) {
      alert("Please log in to add items to your cart.");
      navigate(`/login?path=${  window.location.pathname}`);
      return;
    }
    if (!supermarket) {
        alert("Supermarket details not loaded yet. Please wait.");
        return;
    }
    const userId = currentUser.uid;
    const cartRef = doc(db, 'Carts', userId);
    const newProductEntry = {
        productId,
        quantity: 1,
        name: productDetails.display_name,
        price: productDetails.inventoryDetails.sale_price,
    };
    try {
      let updatedCartData = null;
      
      await runTransaction(db, async (t) => {
        const cartDoc = await t.get(cartRef);
        let cartData = {}; 
        
        // Always ensure we have a valid cartData object
        if (cartDoc.exists()) {
            cartData = cartDoc.data();
            if (typeof cartData !== 'object' || cartData === null) cartData = {}; 
        }
        
        // Ensure supermarketCartInstances is a valid object
        let supermarketCartInstances = cartData[supermarketId] || {};
        if (typeof supermarketCartInstances !== 'object' || Array.isArray(supermarketCartInstances) || supermarketCartInstances === null) {
            supermarketCartInstances = {};
        }
        
        // Find active cart instance
        let activeCartInstanceId = null;
        let activeCartInstance = null;
        for (const instanceId in supermarketCartInstances) {
          if (supermarketCartInstances.hasOwnProperty(instanceId) && supermarketCartInstances[instanceId] && supermarketCartInstances[instanceId].status === 'active') {
            activeCartInstanceId = instanceId;
            activeCartInstance = supermarketCartInstances[instanceId];
            break;
          }
        }
        
        // Update or create cart instance
        if (activeCartInstance) {
          const productIndex = activeCartInstance.products.findIndex(p => p.productId === productId);
          if (productIndex > -1) {
            activeCartInstance.products[productIndex].quantity += 1;
          } else {
            activeCartInstance.products.push(newProductEntry);
          }
          activeCartInstance.lastUpdatedAt = serverTimestamp();
          // Mark cart instance as having unseen updates for notification
          activeCartInstance.hasUnseenUpdate = true;
        } else {
          activeCartInstanceId = `cart_instance_${Date.now()}`;
          activeCartInstance = {
            instanceId: activeCartInstanceId,
            products: [newProductEntry],
            status: 'active',
            supermarketName: supermarket.display_name,
            createdAt: serverTimestamp(),
            lastUpdatedAt: serverTimestamp(),
            hasUnseenUpdate: true, // Mark new cart instance as having unseen updates
          };
        }
        
        // Update the cart instances
        supermarketCartInstances[activeCartInstanceId] = activeCartInstance;
        const updateData = { 
          ...cartData, 
          [supermarketId]: supermarketCartInstances, 
          lastGlobalUpdate: serverTimestamp() 
        };
        
        // Always use set with merge to ensure document exists
        t.set(cartRef, updateData, { merge: true });
        
        // Store only the active cart instance data for notification
        updatedCartData = activeCartInstance ? { [activeCartInstanceId]: activeCartInstance } : {};
      });
      
      // Find the added item's new quantity
      const addedItem = {
        name: productDetails.display_name,
        quantity: 1, // Always show 1 for the item just added
        price: productDetails.inventoryDetails.sale_price
      };
      
      // Get active cart instance ID for checkout
      const activeCartInstanceId = getActiveCartInstanceId(updatedCartData);
      
      // Show enhanced notification
      showNotification({
        type: 'cart',
        title: 'Item Added to Cart',
        message: '',
        duration: 6000, // 6 seconds
        cartData: {
          addedItem,
          cartItems: formatCartItems(updatedCartData),
          totalItems: calculateTotalItems(updatedCartData),
          totalPrice: calculateTotalPrice(updatedCartData),
          supermarketName: supermarket.display_name
        },
        onViewCart: () => navigate('/carts'),
        onCheckout: () => {
          if (activeCartInstanceId) {
            navigate(`/checkout/${supermarketId}/${activeCartInstanceId}`);
          } else {
            navigate(`/checkout/${supermarketId}`);
          }
        },
        userId: currentUser.uid,
        supermarketId: supermarketId
      });
    } catch (error) {
      logger.error("Error adding to cart: ", error);
      alert(`Failed to add product to cart. ${  error.message}`);
    }
  };

  const handleAddToShoppingList = (productId, productDetails) => {
    if (!currentUser) {
      showNotification({
        type: 'info',
        title: 'Sign in required',
        message: 'Please log in to add items to a shopping list.',
        duration: 4000,
      });
      navigate(`/login?path=${window.location.pathname}`);
      return;
    }
    if (!supermarket) {
      alert('Supermarket details not loaded yet. Please wait.');
      return;
    }
    if (!productDetails.inventoryDetails || productDetails.inventoryDetails.quantity <= 0) {
      return;
    }
    setListProductContext({
      productId,
      name: productDetails.display_name,
      price: productDetails.inventoryDetails.sale_price,
      quantity: 1,
    });
    setShowAddToListModal(true);
  };

  const handleHideReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to hide this review?')) return;
    setHidingReviewIds(ids => [...ids, reviewId]);
    try {
      await updateDoc(doc(db, 'SupermarketReviews', reviewId), { hidden: true });
    } catch (err) {
      alert(`Failed to hide review: ${  err.message}`);
    }
    setHidingReviewIds(ids => ids.filter(id => id !== reviewId));
  };

  const handleUnhideReview = async (reviewId) => {
    if (!window.confirm('Make this review visible to all users?')) return;
    setHidingReviewIds(ids => [...ids, reviewId]);
    try {
      await updateDoc(doc(db, 'SupermarketReviews', reviewId), { hidden: false });
    } catch (err) {
      alert(`Failed to unhide review: ${  err.message}`);
    }
    setHidingReviewIds(ids => ids.filter(id => id !== reviewId));
  };

  // Handle SEO routing - find supermarket by SEO name if provided
  useEffect(() => {
    const findSupermarketBySeoName = async () => {
      if (effectiveSeoName && !supermarketId) {
        try {
          // Query supermarkets by SEO name
          const supermarketsRef = collection(db, 'Supermarkets');
          const seoQuery = query(supermarketsRef, where('seo_name', '==', effectiveSeoName));
          const seoSnapshot = await getDocs(seoQuery);
          
          if (!seoSnapshot.empty) {
            const foundSupermarket = seoSnapshot.docs[0];
            setSeoSupermarketId(foundSupermarket.id);
            
            // Update URL to include the actual supermarket ID for consistency
            const newPath = `/supermarkets/${foundSupermarket.id}/products/${effectiveSeoName}`;
            if (location.pathname !== newPath) {
              navigate(newPath, { replace: true });
            }
          } else {
            // SEO name not found, redirect to 404 or search
            navigate('/supermarkets', { replace: true });
          }
        } catch (error) {
          logger.error("Error finding supermarket by SEO name:", error);
          navigate('/supermarkets', { replace: true });
        }
      }
    };

    findSupermarketBySeoName();
  }, [effectiveSeoName, supermarketId, navigate, location.pathname]);

  // Check if effectiveSeoName is actually a product ID and redirect if needed
  useEffect(() => {
    if (effectiveSeoName && supermarketId && !seoSupermarketId) {
      logger.debug('[SupermarketProducts] Checking effectiveSeoName:', effectiveSeoName, 'supermarketId:', supermarketId);
      
      // Check if effectiveSeoName looks like a product ID (Firebase document ID format)
      if (effectiveSeoName.length === 20 && /^[a-zA-Z0-9]+$/.test(effectiveSeoName)) {
        // This looks like a product ID, redirect to product details
        logger.debug('[SupermarketProducts] effectiveSeoName appears to be a product ID, redirecting to product details');
        navigate(`/supermarkets/${supermarketId}/products/${effectiveSeoName}`, { replace: true });
        return;
      }
      
      // Check if effectiveSeoName is actually a supermarket SEO name
      const checkIfSupermarketSeo = async () => {
        try {
          const supermarketsRef = collection(db, 'Supermarkets');
          const seoQuery = query(supermarketsRef, where('seo_name', '==', effectiveSeoName));
          const seoSnapshot = await getDocs(seoQuery);
          
          if (!seoSnapshot.empty) {
            // This is a supermarket SEO name, not a product ID
            const foundSupermarket = seoSnapshot.docs[0];
            if (foundSupermarket.id !== supermarketId) {
              // Different supermarket, redirect to the correct one
              navigate(`/supermarkets/${foundSupermarket.id}/products/${effectiveSeoName}`, { replace: true });
            }
          }
        } catch (error) {
          logger.error("Error checking if effectiveSeoName is supermarket SEO:", error);
        }
      };
      
      checkIfSupermarketSeo();
    }
  }, [effectiveSeoName, supermarketId, seoSupermarketId, navigate]);

  useEffect(() => {
    const fetchSupermarketDetails = async () => {
      const targetSupermarketId = seoSupermarketId || supermarketId;
      if (!targetSupermarketId) return;

      try {
        const supermarketDoc = await getDoc(doc(db, 'Supermarkets', targetSupermarketId));
        if (supermarketDoc.exists()) {
          const supermarketData = { id: supermarketDoc.id, ...supermarketDoc.data() };
          setSupermarket(supermarketData);
          
          // Fetch supermarket image if available
          if (supermarketData.media && supermarketData.media.length > 0 && supermarketData.media[0].url) {
            try {
              const storage = getStorage();
              const imageRef = ref(storage, supermarketData.media[0].url);
              const imageUrl = await getDownloadURL(imageRef);
              setSupermarketImage(imageUrl);
            } catch (imageError) {
              logger.warn(`Failed to load supermarket image:`, imageError);
            }
          }
        } else {
          logger.error("Supermarket not found");
        }
      } catch (error) {
        logger.error("Error fetching supermarket details:", error);
      }
    };

    const fetchProducts = async () => {
      const targetSupermarketId = seoSupermarketId || supermarketId;
      if (!targetSupermarketId) return;
      
      setLoading(true);
      try {
        const inventoryRef = collection(db, 'Inventory');
        const inventoryQuery = query(inventoryRef, where('supermarket_id', '==', targetSupermarketId));
        const inventorySnapshot = await getDocs(inventoryQuery);
        if (inventorySnapshot.empty) {
          setProducts([]);
          return;
        }
        const productPromises = inventorySnapshot.docs.map(async (itemDoc) => {
          const itemData = itemDoc.data();
          if (!itemData.product_id) return null;
          const productDoc = await getDoc(doc(db, 'Products', itemData.product_id)); 
          if (productDoc.exists()) {
            return { id: productDoc.id, ...productDoc.data(), inventoryDetails: itemData };
          }
          return null;
        });
        const fetchedProducts = (await Promise.all(productPromises)).filter(p => p && p.inventoryDetails && p.inventoryDetails.quantity > 0);
        setProducts(fetchedProducts);
        
        // Reset pagination when products change
        setCurrentPage(1);
        setDisplayedProducts([]);
      } catch (error) {
        logger.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSupermarketDetails().then(() => fetchProducts());
  }, [supermarketId, seoSupermarketId]);

  // Fetch reviews for this supermarket
  useEffect(() => {
    const targetSupermarketId = seoSupermarketId || supermarketId;
    if (!targetSupermarketId) return;
    const reviewsRef = collection(db, 'SupermarketReviews');
    const reviewsQuery = query(reviewsRef, where('supermarketId', '==', targetSupermarketId));
    const unsubscribe = onSnapshot(reviewsQuery, snapshot => {
        setReviews(
          snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(r => r.createdAt) // Only require a timestamp
            .sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.())
        );
      }, (error) => {
        if (error.code === 'permission-denied') {
          logger.warn('[SupermarketProducts] Reviews permission denied - ratings hidden');
          setReviews([]);
        } else {
          logger.error('[SupermarketProducts] Reviews listener error:', error);
        }
      });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [supermarketId, seoSupermarketId]);

  // Submit review
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    const targetSupermarketId = seoSupermarketId || supermarketId;
    if (!targetSupermarketId) return;
    
    setReviewSubmitting(true);
    setReviewError('');
    try {
      await addDoc(collection(db, 'SupermarketReviews'), {
        supermarketId: targetSupermarketId,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: userName || currentUser.displayName || currentUser.email?.split('@')[0] || 'Anonymous',
        rating: reviewRating,
        review: reviewText,
        createdAt: serverTimestamp(),
      });
      setReviewText('');
      setReviewRating(5);
    } catch (err) {
      setReviewError(`Failed to submit review: ${  err.message}`);
    }
    setReviewSubmitting(false);
  };

  // Calculate average rating using all reviews (including hidden)
  const allReviews = reviews; // reviews already contains both visible and hidden if you fetch them all
  const allRatedReviews = allReviews.filter(r => typeof r.rating === 'number');
  const averageRating = allRatedReviews.length
    ? (allRatedReviews.reduce((sum, r) => sum + r.rating, 0) / allRatedReviews.length)
    : 0;

  // Auto-dismiss effect removed - now handled by Toast component with autohide

  // Pagination logic (search + category filter)
  useEffect(() => {
    const startIndex = 0;
    const endIndex = currentPage * productsPerPage;
    const filtered = products.filter(product => {
      const matchSearch =
        !searchTerm ||
        product.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.category || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchCategory =
        !categoryFilter || (product.category || 'Uncategorized') === categoryFilter;
      return matchSearch && matchCategory;
    });

    setDisplayedProducts(filtered.slice(startIndex, endIndex));
    setHasMoreProducts(endIndex < filtered.length);
  }, [products, currentPage, searchTerm, categoryFilter]);

  const loadMoreProducts = () => {
    if (hasMoreProducts) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Reset pagination when search term or category filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter]);

  // Unique categories from products (for filter dropdown)
  const categories = React.useMemo(() => {
    const set = new Set();
    products.forEach(p => set.add(p.category || 'Uncategorized'));
    return ['', ...Array.from(set).sort((a, b) => (a === 'Uncategorized' ? 1 : b === 'Uncategorized' ? -1 : a.localeCompare(b)))];
  }, [products]);

  if (loading) {
    return <Layout><Container className="mt-4 text-center"><p>Loading products...</p></Container></Layout>;
  }

  // Generate SEO-friendly data
  const seoTitle = supermarket ? `${supermarket.display_name} - Products | Pack My Cart` : 'Supermarket Products | Pack My Cart';
  const seoDescription = supermarket ? 
    `Browse products from ${supermarket.display_name}. ${supermarket.address ? `Located at ${supermarket.address}.` : ''} ${supermarket.delivery_enabled ? 'Delivery available.' : ''} Shop online for groceries and household items.` : 
    'Browse supermarket products online. Shop for groceries and household items with delivery and pickup options.';
  const seoKeywords = supermarket ? 
    `${supermarket.display_name}, supermarket, groceries, online shopping, delivery, pickup, ${supermarket.address ? supermarket.address.split(' ').join(', ') : ''}` : 
    'supermarket, groceries, online shopping, delivery, pickup';
  const seoImage = supermarketImage || `${SITE_URL}/logo192.png`;
  const seoUrl = supermarket?.seo_name ? 
    `${SITE_URL}/supermarkets/${supermarket.id}/products/${supermarket.seo_name}` : 
    `${SITE_URL}/supermarkets/${supermarket?.id}/products`;

  return (
    <>
      
      <Layout>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="author" content="Pack My Cart" />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="Pack My Cart" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={seoUrl} />
        <meta property="twitter:title" content={seoTitle} />
        <meta property="twitter:description" content={seoDescription} />
        <meta property="twitter:image" content={seoImage} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={seoUrl} />
        
        {/* Structured Data for Local Business */}
        {supermarket && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "GroceryStore",
              "name": supermarket.display_name,
              "address": supermarket.address ? {
                "@type": "PostalAddress",
                "streetAddress": supermarket.address
              } : undefined,
              "url": seoUrl,
              "image": seoImage,
              "description": seoDescription,
              "aggregateRating": allRatedReviews.length > 0 ? {
                "@type": "AggregateRating",
                "ratingValue": averageRating,
                "reviewCount": allRatedReviews.length,
                "bestRating": 5,
                "worstRating": 1
              } : undefined,
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Products",
                "itemListElement": products.slice(0, 10).map(product => ({
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Product",
                    "name": product.display_name,
                    "description": product.description,
                    "image": product.media?.[0]?.url
                  },
                  "price": product.inventoryDetails?.sale_price,
                  "priceCurrency": "JMD",
                  "availability": product.inventoryDetails?.quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
                }))
              }
            })}
          </script>
        )}
      </Helmet>

      <Container className="mt-4 px-3">
        <Row className="align-items-center mb-1">
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
              &larr; Go Back
            </Button>
          </Col>
          <Col>
            <BreadcrumbNavigation items={breadcrumbItems} />
          </Col>
        </Row>

        {/* Supermarket Header with Image */}
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-4">
          {/* Supermarket Image */}
          <div className="me-0 me-md-4 mb-3 mb-md-0" style={{ width: '100px', height: '100px', flexShrink: 0 }}>
            {supermarketImage ? (
              <Image 
                src={supermarketImage} 
                alt={supermarket?.display_name || 'Supermarket'} 
                className="img-fluid rounded shadow-sm"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  logger.warn('Supermarket image failed to load');
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div 
                className="d-flex align-items-center justify-content-center bg-light rounded shadow-sm"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  fontSize: '0.8rem',
                  color: '#6c757d'
                }}
              >
                No Image
              </div>
            )}
          </div>
          
          {/* Supermarket Details */}
          <div className="flex-grow-1 w-100">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
              <h2 className="mb-0 text-break fs-4 fs-md-3">{supermarket?.display_name || 'Supermarket'} - Products</h2>
              <ShareButton
                url={seoUrl}
                title={seoTitle}
                text={seoDescription}
                variant="outline-secondary"
                size="sm"
              />
            </div>
            
            {/* Supermarket Address & Phone */}
            {(supermarket?.address || supermarket?.delivery_phone) && (
              <p className="mb-2 text-muted text-break small" style={{ fontSize: '1rem' }}>
                {supermarket.address && (
                  <>
                    <strong>Address:</strong> {supermarket.address}
                  </>
                )}
                {supermarket.address && supermarket.delivery_phone && (
                  <span className="mx-2" aria-hidden="true">·</span>
                )}
                {supermarket.delivery_phone && (
                  <>
                    <strong>Phone:</strong>{' '}
                    <a href={`tel:${supermarket.delivery_phone}`} className="text-muted text-decoration-none">
                      {supermarket.delivery_phone}
                    </a>
                  </>
                )}
              </p>
            )}
            
            {/* Delivery Service Indicator */}
            {supermarket?.delivery_enabled && (
              <div className="mb-2">
                <span className="badge bg-success text-white me-2">
                  <i className="fas fa-truck me-1"></i>
                  Delivery Available
                </span>
                <small className="text-muted">This supermarket offers delivery service for orders</small>
              </div>
            )}
            
            {/* Opening Hours */}
            {supermarket?.opening_hours && supermarket.opening_hours.length > 0 && (
              <div className="mb-2">
                <strong className="d-block mb-1">Opening Hours:</strong>
                <div className="ms-3">
                  {supermarket.opening_hours.map((hour, idx) => (
                    <div key={idx} className="small text-muted mb-1">
                      <span className="fw-semibold">{hour.day}:</span> {hour.hours}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Show average rating if there are reviews */}
        <div className="mb-3">
          <strong>Supermarket Rating:&nbsp;</strong>
          {allRatedReviews.length > 0 ? (
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-1">
              <span style={{ color: '#f5c518', fontSize: '1.2em' }}>
                {'★'.repeat(Math.round(averageRating))}
                {'☆'.repeat(5 - Math.round(averageRating))}
              </span>
              <span className="text-muted small">
                {averageRating.toFixed(1)} / 5 ({allRatedReviews.length} review{allRatedReviews.length > 1 ? 's' : ''})
              </span>
            </div>
          ) : (
            <span className="text-muted">No ratings yet</span>
          )}
        </div>

        {/* --- Search Bar and Category Filter --- */}
        <Row className="mb-4 align-items-center g-2">
          <Col xs={12} md={6}>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-100"
                style={{ fontSize: '16px' }} // Prevents zoom on iOS
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                  style={{
                    fontSize: '16px',
                    minWidth: '44px',
                    position: 'absolute',
                    right: '0',
                    top: '0',
                    height: '100%',
                    borderTopLeftRadius: '0',
                    borderBottomLeftRadius: '0',
                    zIndex: 10
                  }}
                >
                  ×
                </Button>
              )}
            </div>
          </Col>
          <Col xs={12} md={4} lg={3}>
            <Form.Select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              aria-label="Filter by category"
              style={{ fontSize: '16px' }}
            >
              <option value="">All categories</option>
              {categories.filter(c => c !== '').map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {!supermarket && !loading && (
            <p>Supermarket details could not be loaded. Products may be unavailable.</p>
        )}

        {displayedProducts.length === 0 && !loading ? (
          <p>No products found for this supermarket{searchTerm || categoryFilter ? ' with the current search or category filter.' : '.'}</p>
        ) : (
          (() => {
            // Keep fetch/pagination order — do not regroup or resort by category on load more
            const productsPerRow = 4;
            const productRows = [];
            for (let i = 0; i < displayedProducts.length; i += productsPerRow) {
              productRows.push(displayedProducts.slice(i, i + productsPerRow));
            }

            return (
              <div>
                {productRows.map((row, rowIndex) => (
                  <Row key={rowIndex} xs={1} sm={2} md={3} lg={4} className="g-3 mb-4">
                    {row.map(product => (
                      <Col key={product.id}>
                        <div className="card h-100 shadow-sm">
                          <Link to={`/supermarkets/${supermarketId}/products/${getProductShareSlug(product)}`} style={{ textDecoration: 'none', color: 'inherit', display:'flex', flexDirection:'column', flexGrow:1 }}>
                            {product.media && product.media.length > 0 && product.media[0].url ? (
                              <ProductImage 
                                imagePath={product.media[0].url} 
                                altText={product.display_name} 
                              />
                            ) : (
                              <div className="card-img-top" style={{height: '180px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                <span className="small">No Image</span>
                              </div>
                            )}
                            <div className="card-body d-flex flex-column">
                              <div className="mb-2 d-flex flex-column gap-1">
                                <span className="badge bg-secondary text-white small align-self-start">{product.category}</span>
                                {product.inventoryDetails?.discount_deals && product.inventoryDetails.discount_deals.length > 0 && (
                                  <Badge bg="success" className="small align-self-start">
                                    {product.inventoryDetails.discount_deals.length} Deal{product.inventoryDetails.discount_deals.length !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>
                              <h5 className="card-title text-break small">{product.display_name || 'Unnamed Product'}</h5>
                              <p className="card-text mb-1">Price: ${product.inventoryDetails?.sale_price !== undefined ? product.inventoryDetails.sale_price.toFixed(2) : 'N/A'}</p>
                              <p className="card-text mb-auto"><small className="text-muted">Stock: {product.inventoryDetails?.quantity !== undefined ? product.inventoryDetails.quantity : 'N/A'}</small></p>
                            </div>
                          </Link>
                          <div className="card-footer bg-transparent border-top-0 p-2">
                            <div className="d-grid gap-2">
                              <Button 
                                variant="primary" 
                                className="btn-sm"
                                onClick={() => handleAddToCart(product.id, product)}
                                disabled={!product.inventoryDetails || product.inventoryDetails.quantity <= 0 || !currentUser}
                              >
                                {currentUser ? (product.inventoryDetails && product.inventoryDetails.quantity > 0 ? 'Add to Cart' : 'Out of Stock') : 'Login to Add'}
                              </Button>
                              <Button
                                variant="outline-primary"
                                className="btn-sm"
                                onClick={() => handleAddToShoppingList(product.id, product)}
                                disabled={!product.inventoryDetails || product.inventoryDetails.quantity <= 0 || !currentUser}
                              >
                                Add to List
                              </Button>
                              {product.inventoryDetails?.discount_deals && product.inventoryDetails.discount_deals.length > 0 && (
                                <Button 
                                  variant="outline-success" 
                                  size="sm"
                                  onClick={() => handleViewDeals(product)}
                                  className="btn-sm"
                                >
                                  View Deals ({product.inventoryDetails.discount_deals.length})
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                ))}
                
                {hasMoreProducts && displayedProducts.length > 0 && (
                  <Row className="justify-content-center mt-4">
                    <Col xs="auto">
                      <Button
                        variant="primary"
                        onClick={loadMoreProducts}
                        className="px-4"
                        style={{ minWidth: '200px' }}
                      >
                        Load More Products
                      </Button>
                    </Col>
                  </Row>
                )}

                {!hasMoreProducts && displayedProducts.length > 0 && (
                  <Row className="justify-content-center mt-4">
                    <Col xs="auto">
                      <p className="text-muted text-center">No more products to load</p>
                    </Col>
                  </Row>
                )}
              </div>
            );
          })()
        )}

        {/* Reviews Section */}
        <hr className="my-4" />
        <h3>Supermarket Reviews</h3>
        {(() => {
  // For regular users, only show visible reviews
  // For Admins/Assigned Employees, show all reviews (hidden and visible)
  const visibleToAll = reviews.filter(r => !r.hidden);
  const visibleToModerator = canSeeHiddenReviews(userRole, currentUser, supermarket, employeeSupermarketId) ? reviews : visibleToAll;

  if (visibleToModerator.length === 0) return <p>No reviews yet.</p>;

  return visibleToModerator.map(r => (
    <div
      key={r.id}
      className={`mb-3 p-3 border rounded ${r.hidden ? 'bg-warning-subtle' : 'bg-light'}`}
      style={r.hidden ? { opacity: 0.7 } : {}}
    >
      <strong>
        {r.userName || r.userEmail?.split('@')[0] || 'Anonymous'}
      </strong>
      &nbsp;
      <span>
        {'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}
      </span>
      <div>{r.review}</div>
      <small className="text-muted">{r.createdAt?.toDate?.().toLocaleString?.() || ''}</small>
      {r.hidden && canSeeHiddenReviews(userRole, currentUser, supermarket, employeeSupermarketId) && (
        <span className="badge bg-warning text-dark ms-2">Hidden</span>
      )}
      {canModerateReviews(userRole, currentUser, supermarket, employeeSupermarketId) && (
        <>
          {!r.hidden ? (
            <Button
              variant="outline-danger"
              size="sm"
              className="ms-2"
              onClick={() => handleHideReview(r.id)}
              disabled={hidingReviewIds.includes(r.id)}
            >
              {hidingReviewIds.includes(r.id) ? 'Hiding...' : 'Hide'}
            </Button>
          ) : (
            <Button
              variant="outline-success"
              size="sm"
              className="ms-2"
              onClick={() => handleUnhideReview(r.id)}
              disabled={hidingReviewIds.includes(r.id)}
            >
              {hidingReviewIds.includes(r.id) ? 'Making visible...' : 'Make Visible'}
            </Button>
          )}
        </>
      )}
    </div>
  ));
})()}

        {/* Leave a Review */}
        {currentUser && (
          <Form onSubmit={handleReviewSubmit} className="mt-4 mb-5">
            <Form.Group controlId="reviewRating" className="mb-3">
              <Form.Label>Rating: {reviewRating} Star{reviewRating > 1 ? 's' : ''}</Form.Label>
              <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-3">
                <Form.Range
                  min={1}
                  max={5}
                  step={1}
                  value={reviewRating}
                  onChange={e => setReviewRating(Number(e.target.value))}
                  className="flex-grow-1 w-100"
                  style={{ maxWidth: '100%' }}
                />
                <div className="text-warning text-center text-sm-start" style={{ fontSize: '1.1em', minWidth: '80px' }}>
                  {'★'.repeat(reviewRating)}{'☆'.repeat(5 - reviewRating)}
                </div>
              </div>
            </Form.Group>
            <Form.Group controlId="reviewText" className="mb-3">
              <Form.Label>Review</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={reviewText}
                onChange={e => setReviewText(e.target.value)}
                required
                placeholder="Write your review here..."
                className="w-100"
                style={{
                  minHeight: 80,
                  resize: 'vertical'
                }}
              />
            </Form.Group>
            {reviewError && <div className="text-danger mb-2 small">{reviewError}</div>}
            <Button type="submit" variant="success" className="w-100 w-sm-auto" disabled={reviewSubmitting}>
              {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </Form>
        )}
        {!currentUser && (
          <div className="mb-5">
            <p>
              <Link to="/login">Log in</Link> to leave a review.
            </p>
          </div>
        )}


        {/* Discount Deals Modal */}
        <Modal show={showDealsModal} onHide={handleCloseDealsModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Discount Deals for {selectedProductName}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedProductDeals.length === 0 ? (
              <p>No discount deals currently available for this item.</p>
            ) : (
              <div>
                <p className="text-muted mb-3">
                  <strong>Regular Price:</strong> ${selectedProductPrice.toFixed(2)} per item
                </p>
                <ListGroup>
                  {selectedProductDeals.map((deal, index) => {
                    const regularPrice = selectedProductPrice * deal.quantity;
                    const savings = regularPrice - deal.price;
                    const savingsPercentage = ((savings / regularPrice) * 100).toFixed(1);
                    return (
                      <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <strong className="fs-5">{deal.quantity} items</strong>
                              <br />
                              <span className="text-success fw-bold fs-4">${deal.price.toFixed(2)}</span>
                            </div>
                            <Badge bg="success" className="fs-6">
                              Save {savingsPercentage}%
                            </Badge>
                          </div>
                          <div className="text-muted small">
                            <div>Regular price: ${regularPrice.toFixed(2)}</div>
                            <div>You save: <span className="text-success fw-bold">${savings.toFixed(2)}</span></div>
                            <div>Per item: <span className="text-success fw-bold">${(deal.price / deal.quantity).toFixed(2)}</span></div>
                          </div>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
                <div className="mt-3 p-3 bg-light rounded">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    These deals are automatically applied when you add the specified quantity to your cart.
                  </small>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDealsModal}>Close</Button>
          </Modal.Footer>
        </Modal>
        {currentUser && listProductContext && supermarket && (
          <AddToShoppingListModal
            show={showAddToListModal}
            userId={currentUser.uid}
            supermarketId={supermarket.id}
            supermarketName={supermarket.display_name || 'Supermarket'}
            product={listProductContext}
            onHide={() => {
              setShowAddToListModal(false);
              setListProductContext(null);
            }}
            onAdded={(message) => {
              showNotification({
                type: 'success',
                title: 'Shopping List Updated',
                message,
                duration: 5000,
              });
            }}
          />
        )}
      </Container>
    </Layout>
    </>
  );
}
